import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Popover,
    TextField,
    IconButton,
    InputAdornment,
    Button,
    List,
    ListItem,
    ListItemText,
    useTheme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Add from '@mui/icons-material/Add';

import { setLoading } from '../reducers/loadingReducer';
import { addPromptItem } from '../reducers/uiReducer';
import { setSelectedTileId } from '../reducers/uiReducer';
import { fetchPrompts } from '../services/apiService';
import { setUserPrompts } from '../reducers/promptsReducer';
import LoadingSpinner from './LoadingSpinner';
import getPromptIcon from './PromptIcon';
import { text } from 'd3';

const PromptPopover = ({
    id,
    open,
    anchorEl,
    handleClose,
    handleRunPromptCallback,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const userPrompts = useSelector((state) => state.prompts.userPrompts);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredBarPrompts, setFilteredBarPrompts] = useState(userPrompts);

    const fetchUserPrompts = async () => {
        try {
            setIsLoading(true);
            const prompts = await fetchPrompts();
            const promptData = prompts.data || [];
            setFilteredBarPrompts(promptData);
            dispatch(setUserPrompts(promptData));
        } catch (error) {
            console.error('Error fetching prompts:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (open && userPrompts.length === 0) {
            fetchUserPrompts();
        }
    }, [open]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const filtered = userPrompts.filter((prompt) =>
                prompt.title?.toLowerCase().includes(searchTerm.toLowerCase()),
            );
            setFilteredBarPrompts(filtered);
        }, 200);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    const handleClearSearch = () => setSearchTerm('');

    const handleRunPrompt = (prompt) => {
        handleClose();
        handleRunPromptCallback(prompt);
    };

    const handleAddPrompt = (prompt) => {
        const promptItem = {
            id: prompt.id,
            showIcon: true,
            type: 'manage_saved_prompts',
            text: prompt.promptText,
            description: prompt.description,
        };

        //handleClose();
        dispatch(addPromptItem(promptItem));
    };

    const handleManagePrompts = () => {
        dispatch(setSelectedTileId('manage_saved_prompts'));
        handleClose();
    };

    const getListItemStyles = {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1,
        border: `1px solid ${theme.palette.divider}`,
        //borderRadius: 1,
        color: theme.palette.text.primary,
        backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#fff',
        '&:hover': {
            backgroundColor:
                theme.palette.mode === 'dark'
                    ? theme.palette.action.hover
                    : 'rgba(0, 0, 0, 0.04)',
        },
    };

    return (
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            sx={{ mt: -2 }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Box
                sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top, #1e1e1e, #2e2e2e)'
                            : 'linear-gradient(180deg, #e4f2fb, #f2f2f2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <TextField
                    label="Search"
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={handleClearSearch}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Button variant="outlined" size="small" onClick={handleManagePrompts}>
                    Manage Prompts
                </Button>
            </Box>

            {isLoading ? (
                <Box
                    sx={{
                        height: 450,
                        width: 460,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: theme.palette.background.default,
                    }}
                >
                    <LoadingSpinner />
                </Box>
            ) : filteredBarPrompts.length > 0 ? (
                <List sx={{ height: 450, width: 460, overflowY: 'auto', p: 0 }}>
                    {filteredBarPrompts.map((prompt, index) => (
                        <ListItem key={index} sx={{ p: 0 }}>
                            <Box sx={getListItemStyles}>
                                <Box
                                    sx={{
                                        p: 1,
                                        mr: 2,
                                        borderRadius: 1,
                                        boxShadow: 1,
                                        bgcolor: theme.palette.background.paper,
                                        '&:hover': {
                                            boxShadow: 2,
                                            transform: 'scale(1.05)',
                                        },
                                        '&:active': {
                                            transform: 'scale(0.95)',
                                        },
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleManagePrompts();
                                    }}
                                >
                                    {getPromptIcon(prompt.type)}
                                </Box>

                                <ListItemText
                                    primary={prompt.title}
                                    sx={{
                                        pr: 2,
                                        '& .MuiListItemText-primary': {
                                            fontSize: '14px',
                                            color: (theme) => theme.palette.text.primary,
                                        },
                                    }}
                                />

                                <IconButton
                                    color="secondary"
                                    onClick={() => handleAddPrompt(prompt)}
                                >
                                    <Add sx={{ fontSize: 22 }} />
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    onClick={() => handleRunPrompt(prompt)}
                                >
                                    <PlayArrowIcon sx={{ fontSize: 22 }} />
                                </IconButton>
                            </Box>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box
                    sx={{
                        p: 2,
                        textAlign: 'center',
                        color: theme.palette.text.secondary,
                        bgcolor: theme.palette.background.default,
                    }}
                >
                    No prompts found.
                </Box>
            )}
        </Popover>
    );
};

export default PromptPopover;
